import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { FirestoreDataService } from 'src/app/services/firestore-data.service';
import type {
  PCMData,
  PCMIds,
  PCMInputs,
  PCMInternalRuntime,
  PCMParams,
  PCMDomElements,
  PCMSettings,
  PCMSettingsInternal,
  SuperdrawPCMRuntime,
  UserEntriesPrize,
  UserEntriesResponse,
} from './superdraw.types';

@Component({
  selector: 'app-superdraw',
  templateUrl: './superdraw.html',
  styleUrls: ['./superdraw.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SuperDraw implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('pcmParent', { static: false }) pcmParentRef!: ElementRef;

  private readonly fallbackDays = [-1, -1, 1, -1, 0, 2, 2, 2, 2, 2, 2, 2];
  private destroy$ = new Subject<void>();
  private isViewReady = false;
  private hasEntriesLoaded = false;
  private pendingRenderFrame: number | null = null;
  private lastRenderedStateKey: string | null = null;

  private PCM: SuperdrawPCMRuntime | null = null;
  private preloadImages: string[] = [];
  private pcmData!: PCMData;

  constructor(private router: Router, private authService: FirestoreDataService, private translate: TranslateService) {
    this.pcmData = {
      days: [...this.fallbackDays],
      theme: 3, // SUPER_DRAW
      backButton: {
        url: "/mainTabs/tabs/home",
      },
      ctaButton: {
        url: "/mainTabs/tabs/home",
        text: this.translate.instant("issue-award.button"),
      },
      prize: {
        image: '/assets/superdraw_pcm_images/images/icon_credit_0.png',
        text: '£5',
      },
    };
  }

  ngOnInit(): void {
    this.initializePCM();

    this.authService.getV2CloudFunction("getuserentries", {}).pipe(
      takeUntil(this.destroy$)
    ).subscribe((response: UserEntriesResponse | null | undefined) => {
        if (response) {
            this.pcmData.days = this.normalizeDays(response?.days);
            this.pcmData.labels = this.normalizeLabels(
              response?.labels ?? response?.dates ?? response?.date
            );

            if (response?.prize) {
                this.pcmData.prize = {
                    image: response.prize.image_url ?? this.pcmData.prize?.image ?? '',
                    text: this.formatPrizeText(response.prize),
                };
            }
        }

        this.hasEntriesLoaded = true;
        this.renderPCM();
    }, () => {
        this.hasEntriesLoaded = true;
        this.renderPCM();
    });
  }

  private navigateFromPcmUrl(url: string): void {
    if (!url) {
      return;
    }

    if (url.startsWith('http')) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    void this.router.navigateByUrl(url);
  }

  formatPrizeText(prize: UserEntriesPrize | null | undefined): string {
    if (!prize) return "";

    const value = prize.value ?? "";
    const text = prize.prize_text ?? "";

    if (prize.type === "discount_voucher") {
      return `${value}${text}`.trim();
    }

    return `${value} ${text}`.trim();
  }

  ngAfterViewInit(): void {
    this.isViewReady = true;
    this.renderPCM();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.pendingRenderFrame !== null) {
      cancelAnimationFrame(this.pendingRenderFrame);
      this.pendingRenderFrame = null;
    }

    if (this.PCM && this.PCM.Remove) {
      this.PCM.Remove();
    }
  }

  private normalizeDays(days: unknown): number[] {
    if (!Array.isArray(days) || days.length === 0) {
      return [...this.fallbackDays];
    }

    const normalizedDays = days
      .map((day: unknown) => Number(day))
      .filter((day: number) => !Number.isNaN(day));

    return normalizedDays.length > 0 ? normalizedDays : [...this.fallbackDays];
  }

  private normalizeLabels(labels: unknown): Array<number | string> | undefined {
    if (!Array.isArray(labels) || labels.length === 0) {
      return undefined;
    }

    const normalizedLabels = labels
      .map((label: unknown) => typeof label === 'string' || typeof label === 'number' ? label : null)
      .filter((label: number | string | null): label is number | string => label !== null);

    return normalizedLabels.length > 0 ? normalizedLabels : undefined;
  }

  private renderPCM(): void {
    if (!this.isViewReady || !this.hasEntriesLoaded || !this.PCM || !this.pcmParentRef?.nativeElement) {
      return;
    }

    if (!Array.isArray(this.pcmData.days) || this.pcmData.days.length === 0) {
      this.pcmData.days = [...this.fallbackDays];
    }

    const nextStateKey = JSON.stringify({
      days: this.pcmData.days,
      labels: this.pcmData.labels ?? null,
      prize: this.pcmData.prize ?? null,
    });

    if (this.lastRenderedStateKey === nextStateKey) {
      return;
    }

    this.lastRenderedStateKey = nextStateKey;

    if (this.pendingRenderFrame !== null) {
      cancelAnimationFrame(this.pendingRenderFrame);
    }

    this.pendingRenderFrame = requestAnimationFrame(() => {
      this.pendingRenderFrame = null;

      if (!this.PCM || !this.pcmParentRef?.nativeElement) {
        return;
      }

      this.PCM.Remove?.();

      const preload: false | string[] =
        !this.PCM.preloadRequest && this.preloadImages.length > 0
          ? this.preloadImages
          : false;

      if (this.PCM.preloadRequest) {
        this.PCM.Init(false, this.pcmParentRef.nativeElement, this.pcmData, null);
      } else {
        this.PCM.Init(preload, this.pcmParentRef.nativeElement, this.pcmData, null);
      }
    });
  }

  private initializePCM(): void {
    var PCM_THEMEs =
    {
      VALENTINE:1,
      EASTER:2,
      SUPER_DRAW:3
    }
    const PCM = {
      online : false,
      isReady : false,
      preloadRequest : false,
      domElements : null as PCMDomElements | null,
      params : {} as PCMParams,
      defaultSettings : {} as PCMSettingsInternal,
      settings : {} as PCMSettingsInternal,
      temp: {} as Record<string, unknown>,
      _ids : {} as PCMIds,
      inputs : {} as PCMInputs,
      grid: null,
      data: this.pcmData,
      EffectsManager: undefined as PCMInternalRuntime['EffectsManager'],
      getRandomFromArray: undefined as PCMInternalRuntime['getRandomFromArray'],
      Petals: undefined as PCMInternalRuntime['Petals'],
      Wind: undefined as PCMInternalRuntime['Wind'],
      Cupid: undefined as PCMInternalRuntime['Cupid'],
      FLAGs:{
      },
      ANIMs:
      {
        DEFAULT_FLIP:false,
        CUPID:false,
        PETALS:false,
        WIND:false,
        EASTER_EGG:false,
        GOLDEN_BOX:false,
      },
      THEME_NAMEs : {
          [PCM_THEMEs.VALENTINE]: "valentine",
          [PCM_THEMEs.EASTER]: "easter",
          [PCM_THEMEs.SUPER_DRAW]: "super_draw"
      },
      MAX_SYMBOLs : {
          [PCM_THEMEs.VALENTINE]: 7,
          [PCM_THEMEs.EASTER]: 12,
          [PCM_THEMEs.SUPER_DRAW]: 12
      },
      spinner: null,
      STATEs : {
        DEFAULT : 0,
        ZOOMED : 1
      },
      PRIZE_STATEs : {
        MISSED : -1,
        AVAILABLE : 0,
        CLAIMED : 1,
        LOCKED : 2
      },
      isMobileOrTablet: function() {
          const ua = navigator.userAgent || navigator.vendor || (window as Window & { opera?: string }).opera;
          const isOldMobile = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
          const isIPad = /iPad/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
          const isAndroidTablet = /Android/i.test(ua) && !/Mobile/i.test(ua);
          const isWindowsTablet = /Windows/i.test(ua) && navigator.maxTouchPoints > 1;
          return isOldMobile || isIPad || isAndroidTablet || isWindowsTablet;
      },
      isOnMobile : null,
      isFullView : false,
      assetsHolder : {},
      imagesPath: "/assets/superdraw_pcm_images/images/",
      IMAGEs :[],
      state : 0,
      log : function(message?: unknown): void {
        void message;
      },
      warn : function(message?: unknown): void {
        void message;
      },
      error : function(message?: unknown): void {
        void message;
      },
      _escapeHTML : function(str: unknown): string {
        if (str == null) return '';
        return String(str)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      },
      _activeTimeouts : [] as number[],
      _boundHandlers : {} as Record<string, EventListener>,
      _trackTimeout : function(fn: () => void, delay: number): number {
        const id = window.setTimeout(() => {
          const idx = PCM._activeTimeouts.indexOf(id);
          if (idx !== -1) PCM._activeTimeouts.splice(idx, 1);
          fn();
        }, delay);
        PCM._activeTimeouts.push(id);
        return id;
      },
      _clearAllTimeouts : function() {
        for (const id of this._activeTimeouts) {
          clearTimeout(id);
        }
        this._activeTimeouts = [];
      },
      getRandom : function(min: number, max: number) {
        return Math.random() * (max - min) + min;
      },
      addButtonsListeners : function(buttons: Array<HTMLElement | null>)
      {
        for (var i = 0; i < buttons.length; i++) {
          if (!buttons[i]) {
            continue;
          }
          buttons[i].dataset.screen = "main";
          if(buttons[i].dataset.enabled==null)
            buttons[i].dataset.enabled = "1";
          switch(buttons[i].dataset.anim)
          {
            case "frame":
            buttons[i].addEventListener('click', this._boundHandlers.onClick);
            break;

            case "toggle":
            if(!buttons[i].dataset.toggled)
              buttons[i].dataset.toggled = "0";
            break;

            case "scale":
            if(!buttons[i].dataset.scale)
              buttons[i].dataset.scale = "1";
            buttons[i].addEventListener('click', this._boundHandlers.onClick);
            break;
          }
          if(this.isOnMobile)
          {
            buttons[i].addEventListener('touchstart', this._boundHandlers.onBtnTouched);
          }
          else
          {
            buttons[i].addEventListener('mousedown', this._boundHandlers.onBtnTouched);
          }
        }
      },
      removeButtonsListeners : function(buttons: Array<HTMLElement | null>)
      {
        for (var i = 0; i < buttons.length; i++) {
          if (!buttons[i]) {
            continue;
          }
          switch(buttons[i].dataset.anim)
          {
            case "frame":
            buttons[i].removeEventListener('click', this._boundHandlers.onClick);
            break;

            case "scale":
            buttons[i].removeEventListener('click', this._boundHandlers.onClick);
            break;
          }
          if(this.isOnMobile)
          {
            buttons[i].removeEventListener('touchstart', this._boundHandlers.onBtnTouched);
          }
          else
          {
            buttons[i].removeEventListener('mousedown', this._boundHandlers.onBtnTouched);
          }
        }
      },
      ASSETs : {
        IMAGE : "img"
      },
      RESCALE_MODEs : {
        WIDTH : 0,
        HEIGHT : 1,
        MIN : 2,
        MAX : 3,
        VALUE : 4
      },
      ORIENTATIONs : {
        PORTRAIT : 0,
        LANDSCAPE : 1,
        BOTH : 2
      },
      SHAPEs :
      {
        SPRITE : 0,
        CIRCLE : 1
      },
      Preload : function(addImages: false | string[], parent: HTMLElement, data?: PCMData)
      {
        this.loader ={};
        this.loader.assetsLoaded = 0;
        this.loader.assetsToLoad = 0;
        if(parent)
        {
          this.spinner = {};
          this.spinner.domElements = {};
          this.spinner.domElements.parent = parent;
          var spinnerHTML = '<div class="pcm_spinner_p '+this.THEME_NAMEs[this.theme]+'">';
          switch(this.theme)
          {
            case PCM_THEMEs.VALENTINE:
            spinnerHTML = spinnerHTML + '<div class="pcm_spinner"></div><div class="pcm_heart_spinner"></div>';
            break;
            case PCM_THEMEs.EASTER:
            spinnerHTML = spinnerHTML + '<div class="pcm_egg_spinner"></div><div class="pcm_egg_ring"></div>';
            break;
            case PCM_THEMEs.SUPER_DRAW:
            spinnerHTML = spinnerHTML + '<div class="pcm_super_draw_spinner"><div class="super_draw_box_p"><div class="super_draw_box"><div class="super_draw_box_body"><div class="super_draw_box_lid"><div class="super_draw_box_bowtie box_bowtie_active_spinner"></div></div></div></div></div></div>';
            break;
          }
          spinnerHTML = spinnerHTML +'</div>'
          this.spinner.domElements.parent.innerHTML=spinnerHTML;
          if(data){
            this.loader.data=data;
          }

        }
        this.assetsHolder[this.ASSETs.IMAGE]={};
        if(addImages)
        {
          if (Array.isArray(addImages)) {
            for(var no=0; no<addImages.length; no++)
            {
              this.assetsHolder[this.ASSETs.IMAGE]["add_"+no] = {};
              this.assetsHolder[this.ASSETs.IMAGE]["add_"+no].name = addImages[no];
              this.assetsHolder[this.ASSETs.IMAGE]["add_"+no].img = new Image();
              this.assetsHolder[this.ASSETs.IMAGE]["add_"+no].img.onload = function() {PCM.onAssetLoaded();};
              this.assetsHolder[this.ASSETs.IMAGE]["add_"+no].img.onerror = function() {PCM.onAssetLoaded();};
              this.assetsHolder[this.ASSETs.IMAGE]["add_"+no].img.src = addImages[no];
              if(this.spinner){this.loader.assetsToLoad++;}
            }
          }
        }
        if(this.preloadRequest){return;}
        this.preloadRequest = true;
        Object.keys(this.IMAGEs).forEach((img: string) => {
          document.documentElement.style.setProperty("--pcm-"+img+"-url","url("+this.imagesPath+this.themePath+this.IMAGEs[img]+")");
          this.assetsHolder[this.ASSETs.IMAGE][img] = {};
          this.assetsHolder[this.ASSETs.IMAGE][img].name = this.IMAGEs[img];
          this.assetsHolder[this.ASSETs.IMAGE][img].url = this.imagesPath+this.themePath+this.IMAGEs[img];
          this.assetsHolder[this.ASSETs.IMAGE][img].img = new Image();
          this.assetsHolder[this.ASSETs.IMAGE][img].img.onload = function() {PCM.onAssetLoaded();};
          this.assetsHolder[this.ASSETs.IMAGE][img].img.onerror  = function() {PCM.onAssetLoaded();};
          this.assetsHolder[this.ASSETs.IMAGE][img].img.src = this.assetsHolder[this.ASSETs.IMAGE][img].url
          if(this.spinner){this.loader.assetsToLoad++;}
        });
      },
      onAssetLoaded : function () {
        this.loader.assetsLoaded++;
        if(this.loader.data)
        {
          if(this.loader.assetsLoaded>=this.loader.assetsToLoad){
            this.Init(false,this.spinner.domElements.parent,this.loader.data,null);
          }
        }
      },
      InitDefaultSettings:function()
      {
        this.svgs = {};
        this.svgs.enabled = false;
        this.svgs.tick = '';
        this.svgs.cross = '';
        this.svgs.front_face = '';
        this.defaultSettings.ids = {
          main : "pcm",
          snow : "snow"
        };
        this.defaultSettings.canvas = {
          width : 1080,
          height : 1920,
          rescaleMode : this.RESCALE_MODEs.MIN,
          orientation : this.ORIENTATIONs.PORTRAIT
        };
        this.defaultSettings.cards = {
          count : 7,
          interactable : false,
          reveal_delay : 3
        };
        this.defaultSettings.grid = {
          zoom : 2,
          zoom_time : 3,
          width : 1000,
          height : 1800,
          dx : 410,
          dy : 410
        };
        this.defaultSettings.snow = {
          id : this.defaultSettings.ids.snow,
          enabled : true,
          fixed_dt : 0.016,
          dom_scaling : false,
          mobile_ratio : 0.8,
          parent : "container",
          shape : this.SHAPEs.CIRCLE
        };
        this.defaultSettings.anims = {
          cards_appear : {
            enabled : true,
            delay : 50
          },
          card_tick_appear : {
            enabled : true,
            delay : 100
          }
        };
      },
      InitTheme:function()
      {
        if(this.FLAGs.THEME_INITIATED){return;}
        this.FLAGs.THEME_INITIATED=true;
        this.ANIMs.CUPID=false;
        this.themePath = this.THEME_NAMEs[this.theme]+"/";
        this.IMAGEs = {};
        if(this.theme!=PCM_THEMEs.SUPER_DRAW)
        {
          this.IMAGEs["bg"] = "pcm_bg.png";
        }
        this.IMAGEs["atlas"] = "pcm_atlas.png";
        switch(this.theme)
        {
          case PCM_THEMEs.VALENTINE:
          this.IMAGEs["card_front"]="card_front_face.png";
          this.ANIMs.CUPID=true;
          this.ANIMs.PETALS=true;
          document.documentElement.style.setProperty("--pcm-symbol-scale","0.3");
          break;
          case PCM_THEMEs.EASTER:
          this.IMAGEs["card_front"]="card_front_face.png";
          this.ANIMs.WIND=true;
          this.ANIMs.EASTER_EGG=true;
          document.documentElement.style.setProperty("--pcm-symbol-scale","0.35");
          break;
          case PCM_THEMEs.SUPER_DRAW:
          this.IMAGEs["card_front"]="card_front_face.png";
          this.ANIMs.WIND=true;
          this.ANIMs.GOLDEN_BOX=true;
          document.documentElement.style.setProperty("--pcm-symbol-scale","0.4");
          break;
        }
        if(this.ANIMs.CUPID){this.IMAGEs["cupid"]="pcm_cupid.png";}
        if(this.ANIMs.PETALS){this.IMAGEs["petals"]="pcm_petals.png";}
        if(this.IMAGEs["card_front"])
        {
          if(!this.IMAGEs["card_back"])
          {
            document.documentElement.style.setProperty("--pcm-card_back-url","url("+this.imagesPath+this.themePath+this.IMAGEs["card_front"]+")");
          }
        }
      },
      Init:function(preload: false | string[], parent: HTMLElement, data: PCMData, settings: PCMSettings | null)
      {
        this.theme = PCM_THEMEs.VALENTINE;
        if(data.theme){this.theme=data.theme;}
        this.InitTheme();
        if(preload){
          this.Preload(preload,parent,data,settings);
          return;
        }
        if(window.location.hostname!="localhost"){this.online=true;}
        if(this.spinner)
        {
          this.spinner.domElements.parent.innerHTML = '';
          this.spinner.domElements.parent = null;
          this.spinner = null;
        }
        if(this.isReady){return;}
        this.isOnMobile = this.isMobileOrTablet();
        this.isFullView=true;
        PCM._ids.update=window.requestAnimationFrame(this.Update);
        PCM._ids.boundResize = PCM.Resize.bind(PCM);
        window.addEventListener('resize', PCM._ids.boundResize);
        if(settings){this.settings=settings;}
        if(!this.settings.canvas){
          this.settings.canvas = PCM.defaultSettings.canvas;
          this.settings.canvas.aspectRatio = this.settings.canvas.width/this.settings.canvas.height;
          this.settings.canvas.aspectRatioPercentage = this.settings.canvas.aspectRatio*100;
        }
        if(!this.settings.cards){this.settings.cards = PCM.defaultSettings.cards;}
        if(!this.settings.grid){this.settings.grid = PCM.defaultSettings.grid;}
        if(!this.settings.ids){this.settings.ids = PCM.defaultSettings.ids;}
        if(!this.settings.snow){this.settings.snow = PCM.defaultSettings.snow;}
        if(!this.settings.anims){this.settings.anims = PCM.defaultSettings.anims;}
        this.data = data;
        this.settings.cards.count=this.data.days.length;
        if(this.data.theme){this.theme=this.data.theme;}
        if(!parent){this.error("Parent Container Not Found");return;}
        var addHTML = '<div id="'+this.settings.ids.main+'_container" class="pcm_container"><div id="'+this.settings.ids.main+'" class="pcm"></div></div>';
        parent.innerHTML = addHTML;
        this.domElements = {} as PCMDomElements;
        this.domElements.parent = parent;
        this.domElements.self = document.getElementById(this.settings.ids.main);
        this.domElements.container = document.getElementById(this.settings.ids.main+"_container");
        if(this.data.bg)
        {
          this.domElements.self.style.backgroundImage = "url('"+this.data.bg+"')";
        }
        else
        {
          if(this.theme==PCM_THEMEs.SUPER_DRAW)
          {
            this.domElements.self.style.background = "#0b3443";
          }
          else
          {
            this.domElements.self.style.backgroundImage = "var(--pcm-bg-url)";
          }
        }
        this.domElements.self.style.backgroundRepeat = "no-repeat";
        this.domElements.self.style.backgroundPosition = "bottom center";
        this.domElements.self.style.backgroundSize = "cover";
        if(!this.isOnMobile && !this.isFullView)
        {
          this.domElements.self.classList.add("pcm_desktop");
          if(this.settings.canvas.orientation == this.ORIENTATIONs.PORTRAIT)
          {
            this.domElements.self.style.setProperty("--ar", this.settings.canvas.aspectRatio);
          }
        }
        else
        {
          this.domElements.self.classList.add("pcm_mobile");
        }
        addHTML = '';
        if(this.theme==PCM_THEMEs.EASTER){
          addHTML=addHTML+ '<div id="'+this.settings.ids.main+'_bg_top" class="pcm_atlas pcm_atlas_bg_top_easter pcm_bg_top_'+this.THEME_NAMEs[this.theme]+'"></div>';
        }
        if(this.theme==PCM_THEMEs.SUPER_DRAW){
          addHTML=addHTML+ '<div id="'+this.settings.ids.main+'_bg_left" class="pcm_bg_left_'+this.THEME_NAMEs[this.theme]+'" data-resize="scale"><div class="pcm_atlas pcm_atlas_bg_gift_super_draw pcm_bg_left_icon"></div></div>';
          addHTML=addHTML+ '<div id="'+this.settings.ids.main+'_bg_right" class="pcm_bg_right_'+this.THEME_NAMEs[this.theme]+'" data-resize="scale"><div class="pcm_atlas pcm_atlas_bg_gift_super_draw pcm_bg_right_icon"></div></div>';
        }
        addHTML=addHTML+'<div id="'+this.settings.ids.main+'_content" class="pcm_content" data-resize="scale"></div>'+
        '<div id="'+this.settings.ids.main+'_bottom" class="pcm_bot" data-resize="scale"><div id="'+this.settings.ids.main+'_btn_cta" class="pcm_btn pcm_cta_btn" data-id="cta" data-enabled="1" data-anim="scale" style="display:none;">'+PCM._escapeHTML(this.data.ctaButton.text)+'</div></div>'+
        '<div id="'+this.settings.ids.main+'_top_left" class="pcm_top_left" data-resize="scale"><div id="'+this.settings.ids.main+'_btn_back" class="pcm_btn pcm_back_btn" data-id="back" data-enabled="1" data-anim="scale" style="display:none;">↩</div></div>';
        this.domElements.self.innerHTML = addHTML;
        this.domElements.content = document.getElementById(this.settings.ids.main+"_content");
        this.domElements.buttons = {};
        this.domElements.buttons.cta = document.getElementById(this.settings.ids.main+"_btn_cta");
        this.domElements.buttons.back = document.getElementById(this.settings.ids.main+"_btn_back");
        this.addButtonsListeners([this.domElements.buttons.cta,this.domElements.buttons.back]);
        this.InitGrid();
        this.EffectsManager.Init();
        this.params.time_scale = 1;
        if(!this.isOnMobile)
        {
          document.addEventListener("mouseup", this._boundHandlers.onTouchEnd);
          if(!this.isFullView)
          {
            if(this.domElements.container)
            {
              addHTML = '<div id="'+this.settings.ids.main+'_mask_left" class="pcm_mask pcm_mask_left"></div><div id="'+this.settings.ids.main+'_mask_right" class="pcm_mask pcm_mask_right"></div>';
              this.domElements.container.insertAdjacentHTML("beforeend",addHTML);
              this.domElements.masks = {};
              this.domElements.masks.left = document.getElementById(this.settings.ids.main+"_mask_left");
              this.domElements.masks.right = document.getElementById(this.settings.ids.main+"_mask_right");
            }
          }
        }
        else
        {
          document.addEventListener("touchend", this._boundHandlers.onTouchEnd);
        }
        this.isReady = true;
        this.Resize();
      },
      InitGrid:function()
      {
        if(this.grid){return;}
        this.grid = { domElements: {} };
        var addHTML = '<div id="'+this.settings.ids.main+'_grid" class="pcm_grid"></div>';
        if(this.ANIMs.CUPID && PCM.Cupid?.getHTML){
          addHTML = addHTML + PCM.Cupid.getHTML();
        }
        this.domElements.content.insertAdjacentHTML("beforeend",addHTML);
        this.grid.domElements.self = document.getElementById(this.settings.ids.main+"_grid");
        if(this.settings.snow.enabled)
        {
          var snowHTML = '<canvas id="'+this.settings.ids.snow+'_p" class="pcm_snow_p"></canvas>';
          switch(this.settings.snow.parent)
          {
            case "container":
            this.domElements.container.insertAdjacentHTML("beforeend",snowHTML);
            break;
            case "content":
            this.domElements.content.insertAdjacentHTML("beforeend",snowHTML);
            break;
            case "grid":
            this.grid.domElements.self.insertAdjacentHTML("beforeend",snowHTML);
            break;
            case "parent":
            this.domElements.parent.insertAdjacentHTML("beforeend",snowHTML);
            break;
            default:
            break;
          }
        }
        addHTML = '';
        var count = this.settings.cards.count;
        var padding = 50;
        var ratio = this.settings.grid.width/this.settings.grid.height;
        this.settings.grid.cols = Math.ceil(Math.sqrt(count * ratio));
        this.settings.grid.rows = Math.ceil(count / this.settings.grid.cols);
        var gridUnitSize = Math.min(
            (this.settings.grid.width - padding) / this.settings.grid.cols,
            (this.settings.grid.height - padding) / this.settings.grid.rows
        );
        this.settings.grid.dx = gridUnitSize;
        this.settings.grid.dy = gridUnitSize;
        this.settings.grid.size = this.settings.grid.cols * this.settings.grid.rows;
        this.settings.grid.zoom = Math.floor(this.settings.grid.width/this.settings.grid.dx);
        document.documentElement.style.setProperty("--pcm-card-scale", String(gridUnitSize/210));
        this.params.grid = {};
        this.params.grid.no = 0;
        this.params.grid.pos = [];
        var posX = 0;
        var posY = -(this.settings.grid.rows*0.5-0.5)*this.settings.grid.dy;
        var cardsInRow = 0;
        var numberHTML = '';
        var symbolHTML = '';
        var frontHTML = '';
        var backHTML = '';
        var secondaryHTML = '';
        var statusClass = '';
        var prizeState = 0;
        var cardText: string | number = '';
        var cardBackaAddHTML = '';
        for(var i=0; i<this.settings.grid.rows; i++)
        {
          cardBackaAddHTML = '';
          cardsInRow =  this.settings.cards.count-this.params.grid.no;
          if(cardsInRow>this.settings.grid.cols){cardsInRow=this.settings.grid.cols;}
          posX = -(cardsInRow*0.5-0.5)*this.settings.grid.dx;
          for(var j=0; j<this.settings.grid.cols; j++)
          {
            secondaryHTML = '';
            backHTML = '';
            prizeState = this.data.days[this.params.grid.no];
            switch(prizeState)
            {
              case this.PRIZE_STATEs.CLAIMED:
              if(this.svgs.enabled){secondaryHTML = '<div class="pcm_card_overlay"><div class="pcm_card_svg_tick">'+this.svgs.tick+'</div></div>';}
              else
              {
                var tick_style = '';
                if(this.settings.anims.card_tick_appear.enabled){
                  tick_style = 'transform:scale(0); opacity:0;';
                }
                secondaryHTML = '<div class="pcm_card_overlay"><div class="pcm_card_tick pcm_atlas pcm_atlas_tick'+'_'+this.THEME_NAMEs[this.theme]+'" style="'+tick_style+'"></div></div>';
              }
              statusClass = 'achieved';
              break;
              case this.PRIZE_STATEs.MISSED:
              if(this.svgs.enabled){secondaryHTML = '<div class="pcm_card_overlay"><div class="pcm_card_svg_cross">'+this.svgs.cross+'</div></div>';}
              else
              {
                var cross_style = '';
                if(this.settings.anims.card_tick_appear.enabled){
                  cross_style = 'transform:scale(0); opacity:0;';
                }
                secondaryHTML = '<div class="pcm_card_overlay"><div class="pcm_card_cross pcm_atlas pcm_atlas_cross'+'_'+this.THEME_NAMEs[this.theme]+'" style="'+cross_style+'"></div></div>';
              }
              statusClass = 'missed';
              break;
              case this.PRIZE_STATEs.AVAILABLE:
              secondaryHTML = '';
              statusClass = 'available';
              var prizeImg = '';
              var prizeText = '';
              if(this.data.prize){
                prizeImg = '<img class="pcm_card_prize_icon" src="'+PCM._escapeHTML(this.data.prize.image)+'"></img>'
                prizeText = '<h3 class="pcm_card_prize_text">'+PCM._escapeHTML(this.data.prize.text)+'</h3>'
              }
              if(this.theme==PCM_THEMEs.SUPER_DRAW)
              {
                cardBackaAddHTML = '<div class="super_draw_box_p"><div class="super_draw_box"><div class="super_draw_box_body"><div class="super_draw_box_lid"><div class="super_draw_box_bowtie"></div></div></div></div></div>';
              }
              backHTML = '<div class="pcm_card_face pcm_card_back">'+cardBackaAddHTML+'<div class="pcm_card_prize">'+prizeImg+prizeText+'</div></div>';
              break;
              case this.PRIZE_STATEs.LOCKED:
              secondaryHTML = '<div class="pcm_card_lock">🔒</div>';
              secondaryHTML = '<div class="pcm_card_lock"></div>';
              statusClass = 'locked';
              break;
            }
            symbolHTML = '<div class="pcm_atlas pcm_atlas_symbol_'+(this.params.grid.no%this.MAX_SYMBOLs[this.theme])+'_'+this.THEME_NAMEs[this.theme]+' pcm_card_front_symbol '+this.THEME_NAMEs[this.theme]+'"></div>';
            this.params.grid.pos.push({ x: posX, y: posY });
            cardText = this.params.grid.no+1;
            if(this.data.labels){cardText=this.data.labels[this.params.grid.no];}
            if(this.params.grid.no<this.settings.cards.count){
              numberHTML = '<h3 class="pcm_card_number">'+PCM._escapeHTML(cardText)+'</h3>';
              if(this.svgs.enabled){
                frontHTML = '<div class="pcm_card_face pcm_card_front pcm_card_front_svg '+statusClass+'">'+this.svgs.front_face+symbolHTML+numberHTML+secondaryHTML+'</div>';
              }
              else
              {
                frontHTML = '<div class="pcm_card_face pcm_card_front pcm_card_front_img '+statusClass+'">'+symbolHTML+numberHTML+secondaryHTML+'</div>';
              }
              addHTML = addHTML + '<div class="pcm_card_p" data-id="card_p" data-row="'+i+'" data-col="'+j+'" data-no="'+this.params.grid.no+'" data-state="'+prizeState+'" style="left:'+this.params.grid.pos[this.params.grid.no].x+'px; top: '+this.params.grid.pos[this.params.grid.no].y+'px;">'+
                  '<div class="pcm_card '+this.THEME_NAMEs[this.theme]+'" data-id="card" data-no="'+this.params.grid.no+'" data-enabled="1">'+frontHTML+backHTML+
                  '</div>'+
              '</div>';
            }
            posX = posX + this.settings.grid.dx;
            this.params.grid.no++;
          }
          posY = posY + this.settings.grid.dy;
        }
        this.grid.domElements.self.insertAdjacentHTML("beforeend",addHTML);
        this.grid.domElements.cards = this.grid.domElements.self.querySelectorAll('[data-id="card_p"]');
        for(var no=0; no<this.grid.domElements.cards.length; no++){
          if(this.grid.domElements.cards[no].dataset.id=="card_p"){
            if(parseInt(this.grid.domElements.cards[no].dataset.state)==this.PRIZE_STATEs.AVAILABLE)
            {
              this.grid.domElements.todayCard = this.grid.domElements.cards[no].children[0];
              if(this.settings.cards.interactable)
              {
                this.grid.domElements.todayCard.addEventListener('click', this._boundHandlers.onClick);
              }
            }
          }
        }
        this.Animate();
      },
      Animate: function()
      {
        var anmi_timeline = 0;
        var anmi_timeline_1 = 0;
        if(this.settings.anims.cards_appear.enabled)
        {
          this.grid.domElements.cards.forEach((card: Element) => {
              const cardEl = card as HTMLElement;
              cardEl.classList.add("pcm_card_popin");
              cardEl.style.animationDelay = anmi_timeline_1 + "ms";
              anmi_timeline_1=anmi_timeline_1+PCM.settings.anims.cards_appear.delay;
          });
        }
        var anmi_timeline_2 = anmi_timeline_1;
        if(this.settings.anims.card_tick_appear.enabled)
        {
          for(var no=0; no<this.grid.domElements.cards.length; no++){
            if(this.grid.domElements.cards[no].dataset.id=="card_p"){
              var state = parseInt(this.grid.domElements.cards[no].dataset.state);
              if(state==this.PRIZE_STATEs.CLAIMED||state==this.PRIZE_STATEs.MISSED)
              {
                var class_query = 'pcm_card_tick';
                if(state==this.PRIZE_STATEs.CLAIMED){class_query = '.pcm_card_tick';}
                if(state==this.PRIZE_STATEs.MISSED){class_query = '.pcm_card_cross';}
                var dom_el = this.grid.domElements.cards[no].querySelector(class_query);
                if(dom_el)
                {
                  const tickEl = dom_el as HTMLElement;
                  tickEl.classList.add("pcm_card_tick_popin");
                  tickEl.style.animationDelay = anmi_timeline_2 + "ms";
                  anmi_timeline_2 = anmi_timeline_2+this.settings.anims.card_tick_appear.delay;
                }
              }
            }
          }
        }
        anmi_timeline = anmi_timeline_1+anmi_timeline_2;
        if(!this.settings.cards.interactable)
        {
          PCM._trackTimeout(function(){
               PCM.Zoom(true,PCM.grid.domElements.todayCard);
          }, anmi_timeline+500);
        }
      },
      Update : function(ts: number)
      {
        if (!ts) ts = performance.now();
        if(PCM.params)
        {
          if (PCM.params.pre_ts === undefined){PCM.params.pre_ts=ts;}
          PCM.params.dt = (ts - PCM.params.pre_ts)/1000;
          if(PCM.params.dt==0){PCM.params.dt=0.016;}
          else if(PCM.params.dt>0.2){PCM.params.dt=0.016;}
          PCM.params.dt = PCM.params.dt*PCM.params.time_scale;
          PCM.params.pre_ts = ts;
          if(PCM.isReady){
            if(PCM.EffectsManager)
            {
              PCM.EffectsManager.Update(PCM.params.dt);
              PCM.EffectsManager.Draw();
            }
          }
        }
        PCM._ids.update =window.requestAnimationFrame(PCM.Update);
      },
      Resize : function()
      {
        if(!this.params){return;}
        var params = this.params;
        params.screenWidth = this.domElements.parent.clientWidth;
        params.screenHeight = this.domElements.parent.clientHeight;
        params.aspectRatio = params.screenWidth/params.screenHeight;
        this.log(params.screenWidth+"<>"+params.screenHeight);
        if(this.settings.canvas)
        {
          switch(this.settings.canvas.rescaleMode)
          {
            case this.RESCALE_MODEs.WIDTH:
            params.uiScale = params.screenWidth/this.settings.canvas.width;
            break;
            case this.RESCALE_MODEs.HEIGHT:
            params.uiScale = params.screenHeight/this.settings.canvas.height;
            break;
            case this.RESCALE_MODEs.MIN:
            params.uiScale = Math.min(params.screenWidth/this.settings.canvas.width,params.screenHeight/this.settings.canvas.height)
            break;
            case this.RESCALE_MODEs.MAX:
            params.uiScale = Math.max(params.screenWidth/this.settings.canvas.width,params.screenHeight/this.settings.canvas.height)
            break;
          }
        }
        if(!this.domElements){return;}
        var itemsToRescale = this.domElements.self.querySelectorAll("[data-resize]");
        var itemScale = 1;
        itemsToRescale.forEach(function (item: HTMLElement) {
          itemScale = params.uiScale;
          if(item.dataset.scalemul){itemScale = itemScale*parseFloat(item.dataset.scalemul);}
          switch(item.dataset.resize)
          {
            case "scale_w":
            item.style.transform = 'scaleX('+itemScale+')';
            break;

            case "scale_h":
            item.style.transform = 'scaleY('+itemScale+')';
            break;

            case "scale_hp":
            item.style.height = parseInt(item.dataset.initHeight)*itemScale+'px';
            break;

            default:
            item.style.transform = 'scale('+itemScale+','+itemScale+')';
            break;
          }
        });
        if(this.domElements)
        {
          if(this.domElements.masks)
          {
            if(this.domElements.container)
            {
              var delta_width = this.domElements.container.clientWidth-this.domElements.self.clientWidth;
              if(delta_width>0)
              {
                this.domElements.masks.left.style.width = (delta_width*0.5)+'px';
                this.domElements.masks.right.style.width = (delta_width*0.5)+'px';
              }
            }
          }
        }
        if(this.EffectsManager)
        {
          if(this.EffectsManager.canvas)
          {
            this.EffectsManager.Resize(params.screenWidth,params.screenHeight,params.uiScale)
          }
        }
      },
      ResetSelection : function()
      {
        PCM.state = this.STATEs.DEFAULT;
        PCM.Zoom(false,PCM.params.btn);
      },
      Zoom : function (_in: boolean, btn: HTMLElement)
      {
        var zoom = this.settings.grid.zoom;
        var centerX = 0;
          var centerY = 0;
          var delta_x = 0;
          var delta_y = 0;
          PCM.temp.btn = btn;
          var ctaBtnsDelay = 6000;
          switch(this.theme)
          {
            case PCM_THEMEs.VALENTINE:
            ctaBtnsDelay = 6000;
            break;

            case PCM_THEMEs.EASTER:
            ctaBtnsDelay = 3000;
            break;
          }
        if(_in)
        {
          this.params.isZoomed = true;
          var card_p = this.grid.domElements.cards[parseInt(btn.dataset.no)];
          zoom = this.settings.grid.zoom;
          delta_x = (centerX - card_p.offsetLeft)*zoom;
            delta_y = (centerY - card_p.offsetTop)*zoom;
            PCM._trackTimeout(function(){
              PCM.HideSurroundingCards(parseInt(card_p.dataset.row),parseInt(card_p.dataset.col));
              if(PCM.ANIMs.CUPID && PCM.Cupid?.Init){
                PCM.Cupid.Init();
              }
              else if(PCM.ANIMs.EASTER_EGG)
              {
                PCM._trackTimeout(function(){
                PCM.FlipCard(btn,"do");
                PCM._trackTimeout(function(){
                  (btn.querySelector(".pcm_easter_egg_top") as HTMLElement).style.animation = 'pcm_easter_egg_open_anim 1.5s ease forwards 1s';
                  PCM._trackTimeout(function(){
                    var prizeElement = btn.querySelector(".pcm_card_prize") as HTMLElement;
                    prizeElement.style.display = "block";
                    prizeElement.style.animation = 'pcm_easter_prize_open_anim 0.5s ease forwards';
                    PCM._trackTimeout(function(){
                      (btn.querySelector(".pcm_easter_egg_p") as HTMLElement).style.animation = 'pcm_easter_egg_hide_anim 0.2s ease forwards';
                    }, 1700);
                  }, 1500);
                }, 500);
              }, 500);
              }
              else if(PCM.ANIMs.GOLDEN_BOX)
              {
                PCM._trackTimeout(function(){
                PCM.FlipCard(btn,"do");
                PCM._trackTimeout(function(){
                  (btn.querySelector(".super_draw_box_body") as HTMLElement).style.animation = 'super_draw_box_body_anim 1.25s ease forwards 1s';
                  (btn.querySelector(".super_draw_box_lid") as HTMLElement).style.animation = 'super_draw_box_lid_anim 1.25s ease forwards 1s';
                  const bowtie = btn.querySelector(".super_draw_box_bowtie");
                  if (bowtie) {
                    bowtie.classList.add("box_bowtie_active");
                  }
                  PCM._trackTimeout(function(){
                    var prizeElement = btn.querySelector(".pcm_card_prize") as HTMLElement;
                    prizeElement.style.display = "block";
                    prizeElement.style.animation = 'pcm_easter_prize_open_anim 0.5s ease forwards';
                    PCM._trackTimeout(function(){
                      (btn.querySelector(".super_draw_box_p") as HTMLElement).style.animation = 'pcm_easter_egg_hide_anim 0.2s ease forwards';
                    }, 1200);
                  }, 2000);
                }, 500);
              }, 500);
              }
              else
              {
                PCM.FlipCard(btn,"do");
              }
          }, PCM.settings.grid.zoom_time*1000);
          PCM._trackTimeout(function(){
            PCM.ShowCtaButtons();
          }, PCM.settings.grid.zoom_time*1000+ctaBtnsDelay);
        }
        else
        {
          this.params.isZoomed = false;
          PCM.FlipCard(btn,"reset");
          zoom = 1;
        }
          this.grid.domElements.self.style.transform = `translate(${delta_x}px, ${delta_y}px) scale(${zoom})`;

      },
      ShowCtaButtons : function()
      {
        this.domElements.buttons.back.style.display = null;
        this.domElements.buttons.back.classList.add("pcm_pop_in_out");
        this.domElements.buttons.cta.style.display = null;
        this.domElements.buttons.cta.classList.add("pcm_cat_btn_appear");
      },
      HideSurroundingCards: function(c_i: number, c_j: number)
      {
        var delta_i  = 0;
        var delta_j = 0;
        var hide = false;
        for(var no=0; no<this.grid.domElements.cards.length; no++)
        {
          hide = false;
          delta_i = parseInt(this.grid.domElements.cards[no].dataset.row)-c_i;
          delta_j = parseInt(this.grid.domElements.cards[no].dataset.col)-c_j;
          if(Math.abs(delta_i)==1||Math.abs(delta_j)==1){
            hide = true;
          }
          if(hide)
          {
            this.grid.domElements.cards[no].style.animationDelay = null;
            this.grid.domElements.cards[no].classList.remove("pcm_card_popin");
            this.grid.domElements.cards[no].classList.add("pcm_card_fade_in");
          }
        }
      },
      FlipCard : function (card: HTMLElement, action: string)
      {
        if (!card.classList.contains('pcm_transition_anim')){card.classList.add("pcm_transition_anim");}
        switch(action)
        {
          case "do":
          card.classList.add("pcm_flipped");
          break;

          case "reset":
          card.classList.remove("pcm_flipped");
          break;
        }
      },
      onClickHandler : (event: Event): void => {
        const btn = (event.target as HTMLElement).closest('[data-id]') as HTMLElement | null;
        if (!btn) {
          return;
        }
        const buttonId = btn.dataset.id;
        const enabled = parseInt(btn.dataset.enabled || '0', 10);
        if (enabled !== 1) {
          return;
        }
        switch (buttonId) {
          case 'card':
            if (PCM.state === PCM.STATEs.DEFAULT) {
              PCM.state = PCM.STATEs.ZOOMED;
              PCM.params.btn = btn;
              PCM.Zoom(true, btn);
            }
            break;

          case 'back':
            this.navigateFromPcmUrl(PCM.data.backButton.url);
            break;

          case 'cta':
            this.navigateFromPcmUrl(PCM.data.ctaButton.url);
            break;
        }
      },
      onBtnTouched: function(event: Event)
      {
        var btn = event.target as HTMLElement;
        if(btn){PCM.inputs.btnTouched = btn;}
        else{return;}
        var enabled = parseInt(btn.dataset.enabled);
        if(enabled!=1){return;}
        switch(btn.dataset.anim)
        {
          case "scale":
          var btnScale = 0.95 * parseFloat(btn.dataset.scale);
          btn.style.transform = "scale("+btnScale+")";
          break;
        }
      },
      onTouchEnd : function()
      {
        if(PCM.inputs.btnTouched)
        {
          switch(PCM.inputs.btnTouched.dataset.anim)
          {
            case "scale":
            PCM.inputs.btnTouched.style.transform = "scale("+parseFloat(PCM.inputs.btnTouched.dataset.scale)+")";
            PCM.inputs.btnTouched = null;
            break;
          }
        }
      },
      Remove : function()
      {
        this._clearAllTimeouts();
        if(!this.isReady){return;}
        if (this._ids.update) {
              cancelAnimationFrame(this._ids.update);
              this._ids.update = null;
          }
          window.removeEventListener('resize', PCM._ids.boundResize);
          if(!this.isOnMobile){
          document.removeEventListener("mouseup", this._boundHandlers.onTouchEnd);
        }
        else{
          document.removeEventListener("touchend", this._boundHandlers.onTouchEnd);
        }
        this.removeButtonsListeners([this.domElements.buttons.cta,this.domElements.buttons.back]);
          if(this.settings.cards.interactable)
        {
            if (this.grid && this.grid.domElements && this.grid.domElements.cards) {
                this.grid.domElements.cards.forEach((card: Element) => {
                    const innerCard = card.children[0];
                    if (innerCard) innerCard.removeEventListener('click', this._boundHandlers.onClick);
                });
            }
        }
          if (this.domElements && this.domElements.container) {
              this.domElements.container.remove();
          }
          this.domElements = null;
          this.grid = null;
          this.params = {};
          this.isReady = false;
      }
    };

    PCM._boundHandlers.onClick = (e: Event) => PCM.onClickHandler(e);
    PCM._boundHandlers.onBtnTouched = (e: Event) => PCM.onBtnTouched(e);
    PCM._boundHandlers.onTouchEnd = () => PCM.onTouchEnd();

    PCM.InitDefaultSettings();
    PCM.getRandomFromArray = <T,>(arr: readonly T[]): T => arr[Math.floor(Math.random() * arr.length)];
    PCM.EffectsManager = {
      parent : PCM,
      explosions : [],
      Init : function()
      {
        this.isReady = false;
        this.settings = this.parent.settings.snow;
        this.domElements = {} as PCMDomElements;
        this.domElements.self = document.getElementById(this.settings.id+"_p");
        if(!this.domElements.self)
        {
          var snowHTML = '<canvas id="'+this.settings.id+'_p" class="pcm_snow_p"></canvas>';
          if(this.settings.enabled)
          {
            switch(this.settings.parent)
            {
              case "container":
              this.parent.domElements.container.insertAdjacentHTML("beforeend",snowHTML);
              break;
              case "content":
              this.parent.domElements.content.insertAdjacentHTML("beforeend",snowHTML);
              break;
              case "grid":
              this.parent.grid.domElements.self.insertAdjacentHTML("beforeend",snowHTML);
              break;
              case "parent":
              this.parent.domElements.parent.insertAdjacentHTML("beforeend",snowHTML);
              break;
              default:
              break;
            }
          }
          this.domElements.self = document.getElementById(this.settings.id+"_p");
        }
        this.canvas = this.domElements.self;
        this.ctx = this.canvas.getContext("2d");
        this.params = {};
        this.parent.Resize();
        this.Resize(PCM.params.screenWidth,PCM.params.screenHeight,PCM.params.uiScale);
        if(PCM.ANIMs.PETALS){PCM.Petals.Init(this.canvas,this.ctx,this.params.width,this.params.height);}
        if(PCM.ANIMs.WIND){PCM.Wind.Init(this.canvas,this.ctx,this.params.width,this.params.height);}
        this.isReady=true;
      },
      Update : function(dt: number)
      {
        if(!this.isReady){return;}
        if(this.settings.fixed_dt>0){dt=this.settings.fixed_dt;}
        if(this.parent.ANIMs.PETALS){PCM.Petals.Update(dt);}
        if(this.parent.ANIMs.WIND){PCM.Wind.Update(dt);}
      },
      Draw : function() {
        if(!this.isReady){return;}
          this.ctx.clearRect(0, 0, this.params.width, this.params.height);
          if(this.parent.ANIMs.PETALS){PCM.Petals.Draw();}
          if(this.parent.ANIMs.WIND){PCM.Wind.Draw();}
      },
      Resize : function(screenWidth: number, screenHeight: number, uiScale: number) {
        if(this.settings.dom_scaling)
        {
          this.params.height = Math.floor((screenHeight*1.1)/uiScale);
          if(PCM.isOnMobile||PCM.isFullView){
            this.params.width = Math.floor(screenWidth/uiScale);
          }
          else
          {
            this.params.width = this.parent.settings.canvas.aspectRatio*Math.floor(screenHeight/uiScale);
          }
        }
        else
        {
          uiScale = 1;
          this.params.height = Math.floor((screenHeight*1.1)/uiScale);
          if(PCM.isOnMobile||PCM.isFullView){
            this.params.width = Math.floor(screenWidth/uiScale);
          }
          else
          {
            this.params.width = this.parent.settings.canvas.aspectRatio*Math.floor(screenHeight/uiScale);
          }
        }
        this.canvas.style.width = this.params.width+"px";
        this.canvas.style.height = this.params.height+"px";
        this.canvas.width = this.params.width;
        this.canvas.height = this.params.height;
        if(this.parent.ANIMs.PETALS){PCM.Petals.Resize(this.params.width,this.params.height);}
        if(this.parent.ANIMs.WIND){PCM.Wind.Resize(this.params.width,this.params.height);}
      }
    };
    PCM.Wind = {
        parent: PCM,
        particles: [],
        ctx: null as CanvasRenderingContext2D | null,
        canvas: null as HTMLCanvasElement | null,
        w: 0,
        h: 0,
        isReady: false,
        color: {
            r: 255,
            g: 255,
            b: 255
        },
        Init: function(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, w: number, h: number) {
            this.canvas = canvas;
            this.ctx = ctx;
            this.w = w;
            this.h = h;
            this.particles = [];
            const layers = [
                {count: 5, speed: [80,150], len: [25,35], alpha: [0.3,0.5], width:[1,2]},
                {count: 10, speed: [150,200], len: [30,45], alpha: [0.5,0.75], width:[2,3]},
            ];
            for (const layer of layers) {
                for (let i = 0; i < layer.count; i++) {
                    this.particles.push({
                        layer: layer,
                        x: PCM.getRandom(-w, w),
                        y: PCM.getRandom(0, h),
                        speed: PCM.getRandom(layer.speed[0], layer.speed[1]),
                        len: PCM.getRandom(layer.len[0], layer.len[1]),
                        alpha: PCM.getRandom(layer.alpha[0], layer.alpha[1]),
                        width: PCM.getRandom(layer.width[0], layer.width[1]),
                        amp: PCM.getRandom(20,40),
                        freq: PCM.getRandom(0.01,0.03),
                        phase: PCM.getRandom(0,Math.PI*2)
                    });
                }
            }
            if(this.parent.theme==PCM_THEMEs.SUPER_DRAW)
            {
          this.color = {
                r: 245,
                g: 240,
                b: 182
            };
            }
            this.isReady = true;
        },
        getRGBA: function(alpha: number)
        {
            return `rgba(${this.color.r},${this.color.g},${this.color.b},${alpha})`;
        },
        Update: function(dt: number) {
            if (!this.isReady) return;
            for (let p of this.particles) {
                p.x += p.speed * dt;
                p.phase += dt * p.speed * 0.02;
                if (p.x > this.w + p.len) p.x = -p.len;
            }
        },
        Draw: function() {
            if (!this.isReady) return;
            const ctx = this.ctx;
            for (let p of this.particles) {
                ctx.beginPath();
                ctx.lineWidth = p.width;
                ctx.lineCap = "round";
                const gradient = ctx.createLinearGradient(p.x, p.y, p.x + p.len, p.y);
                gradient.addColorStop(0, this.getRGBA(0));
                gradient.addColorStop(0.2, this.getRGBA(p.alpha));
                gradient.addColorStop(0.8, this.getRGBA(p.alpha));
                gradient.addColorStop(1, this.getRGBA(0));
                ctx.strokeStyle = gradient;
                const steps = 10;
                for (let i = 0; i <= p.len; i += steps) {
                    const px = p.x + i;
                    const py = p.y
                        + Math.sin(i * p.freq + p.phase) * p.amp
                        + Math.sin(i * 0.02 + p.phase*0.5) * 5;
                    if (i === 0) ctx.moveTo(px, py);
                    else ctx.lineTo(px, py);
                }
                ctx.stroke();
            }
        },
        Resize: function(w: number, h: number) {
            this.w = w;
            this.h = h;
        }
    };

    this.PCM = PCM as PCMInternalRuntime;
  }
}
